<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

// Props
const props = defineProps({
  darkMode: {
    type: Boolean,
    default: false
  }
});

// State variables
const notifications = ref([]);
const notificationCount = ref(0);
const showNotificationDropdown = ref(false);
const loadingNotifications = ref(false);
const errorLoading = ref(false);

// Computed for recent notifications (last 5)
const recentNotifications = computed(() => {
  return notifications.value.slice(0, 5);
});

// Fetch notification count on component mount
onMounted(async () => {
  await fetchNotificationCount();
});

// Notification methods
const fetchNotificationCount = async () => {
  try {
    const response = await axios.get('/api/notifications/count');
    notificationCount.value = response.data.count || 0;
  } catch (error) {
    console.error('Error fetching notification count', error);
    notificationCount.value = 0;
  }
};

const fetchNotifications = async () => {
  loadingNotifications.value = true;
  errorLoading.value = false;
  
  try {
    const response = await axios.get('/api/notifications');
    notifications.value = response.data || [];
  } catch (error) {
    console.error('Error fetching notifications', error);
    errorLoading.value = true;
    notifications.value = [];
  } finally {
    loadingNotifications.value = false;
  }
};

const markAsRead = async (id = null) => {
  try {
    const payload = id ? { notification_ids: [id] } : { all: true };
    await axios.post('/api/notifications/mark-read', payload);
    
    // Update the local state
    if (id) {
      const notification = notifications.value.find(n => n.id === id);
      if (notification) notification.read = true;
    } else {
      notifications.value.forEach(n => n.read = true);
    }
    
    // Update count
    await fetchNotificationCount();
  } catch (error) {
    console.error('Error marking notification as read', error);
  }
};

const toggleNotificationDropdown = async (event) => {
  event.stopPropagation();
  showNotificationDropdown.value = !showNotificationDropdown.value;
  
  if (showNotificationDropdown.value) {
    await fetchNotifications();
  }
};

// Close dropdown when clicking outside
const setupOutsideClickListener = () => {
  document.addEventListener('click', function(event) {
    if (showNotificationDropdown.value && 
        !event.target.closest('#notificationDropdown') && 
        !event.target.closest('#notificationIcon')) {
      showNotificationDropdown.value = false;
    }
  });
};

// Setup outside click handler
onMounted(() => {
  setupOutsideClickListener();
});
</script>

<template>
  <div class="nav-icon-wrapper position-relative">
    <a href="#" class="text-white position-relative" id="notificationIcon" @click.stop.prevent="toggleNotificationDropdown($event)">
      <i class="bi bi-bell-fill fs-5" :class="{ 'text-white': darkMode, 'text-dark': !darkMode }"></i>
      <span v-if="notificationCount > 0" class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
        {{ notificationCount > 99 ? '99+' : notificationCount }}
      </span>
    </a>
    
    <!-- Notification dropdown -->
    <div id="notificationDropdown" v-if="showNotificationDropdown" 
         class="notification-dropdown position-absolute end-0 bg-white rounded shadow"
         @click.stop>
      <div class="notification-dropdown-header p-3 border-bottom d-flex align-items-center justify-content-between">
        <h6 class="mb-0 fw-bold">Notifikationer</h6>
        <button v-if="notifications.length > 0 && notificationCount > 0" 
                @click="markAsRead()" 
                class="btn btn-sm btn-link text-decoration-none p-0">
          Marker alle som læst
        </button>
      </div>
      
      <div class="notification-dropdown-body">
        <!-- Loading spinner -->
        <div v-if="loadingNotifications" class="text-center py-4">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Indlæser...</span>
          </div>
          <p class="text-muted mt-2 mb-0">Indlæser notifikationer...</p>
        </div>
        
        <!-- Error state -->
        <div v-else-if="errorLoading" class="text-center py-3">
          <i class="bi bi-exclamation-triangle text-warning fs-4"></i>
          <p class="text-muted mt-2 mb-0">Der opstod en fejl ved hentning af notifikationer.</p>
        </div>
        
        <!-- No notifications message -->
        <div v-else-if="notifications.length === 0" class="text-center py-3">
          <i class="bi bi-bell-slash text-muted fs-4"></i>
          <p class="text-muted mt-2 mb-0">Ingen notifikationer at vise</p>
        </div>
        
        <!-- Notification list -->
        <ul v-else class="list-unstyled mb-0">
          <li v-for="notification in recentNotifications" :key="notification.id" 
              class="notification-item p-3 border-bottom" 
              :class="{ 'unread': !notification.read }">
            <a :href="notification.link" class="notification-link">
              <div class="d-flex">
                <div class="notification-icon me-3">
                  <i class="bi" :class="notification.icon || 'bi-bell'"></i>
                </div>
                <div class="notification-content flex-grow-1">
                  <p class="notification-text mb-1">{{ notification.message }}</p>
                  <small class="notification-time text-muted">{{ notification.time }}</small>
                </div>
                <div v-if="!notification.read" class="notification-action">
                  <button @click.stop="markAsRead(notification.id)" class="btn btn-sm btn-link p-0">
                    <i class="bi bi-check-circle"></i>
                  </button>
                </div>
              </div>
            </a>
          </li>
        </ul>
      </div>
      
      <div v-if="notifications.length > 5" class="notification-dropdown-footer p-2 border-top text-center">
        <a href="#" class="text-decoration-none">Se alle notifikationer</a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-dropdown {
  min-width: 320px;
  max-width: 400px;
  z-index: 1050;
  top: 100%;
  right: -100px;
  margin-top: 0.75rem;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transform-origin: top right;
  animation: dropdownFade 0.2s ease;
}

@keyframes dropdownFade {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.notification-dropdown-body {
  max-height: 300px;
  overflow-y: auto;
}

.notification-item {
  transition: background-color 0.2s ease;
}

.notification-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.notification-item:hover {
  background-color: #f8f9fa;
}

.notification-item.unread {
  background-color: #f0f4ff;
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e9ecef;
}

.notification-text {
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
  color: #212529;
}

.notification-time {
  font-size: 0.8rem;
}

.notification-action {
  align-self: flex-start;
  padding-left: 0.5rem;
}

/* Mobile adjustments for notification dropdown */
@media (max-width: 576px) {
  .notification-dropdown {
    position: fixed;
    width: calc(100% - 2rem);
    left: 1rem;
    right: 1rem;
    top: auto;
    bottom: 1rem;
    margin-top: 0;
    max-width: none;
  }
}
</style>
